import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { catchError, map, of } from 'rxjs';
import { AccountService } from '../account.service';
import { KiMarkComponent } from '../../components/ki/ki-mark/ki-mark.component';
import { KontoSektionComponent } from '../../components/konto-sektion/konto-sektion.component';
import { copyText } from '../../shared/clipboard';

const KOPIERT_ANZEIGE_MS = 1600;

/** Snippet aus page-ki-einstellungen.jsx:309-316; der Token bleibt Platzhalter, ein echter steht nie im Konto. */
function mcpSnippet(serverAdresse: string): string {
  return [
    '{',
    '  "mcpServers": {',
    '    "testoffice": {',
    `      "url": "${serverAdresse}",`,
    '      "headers": { "Authorization": "Bearer <DEIN_API_TOKEN>" }',
    '    }',
    '  }',
    '}',
  ].join('\n');
}

/** MCP-Hinweis im Konto (page-ki-einstellungen.jsx:305-339); fehlt, solange die Sichtbarkeit nicht sicher feststeht. */
@Component({
  selector: 'app-ki-mcp-block',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, KiMarkComponent, KontoSektionComponent],
  templateUrl: './ki-mcp-block.component.html',
  styleUrl: './ki-mcp-block.component.scss',
})
export class KiMcpBlockComponent {
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);

  // Die Instanz liefert FE und MCP-Server unter derselben Adresse aus (Entscheidung NEX-1573).
  protected readonly serverAdresse = `${inject(DOCUMENT).location.origin}/mcp`;
  protected readonly snippet = mcpSnippet(this.serverAdresse);

  protected readonly sichtbar = toSignal(
    inject(AccountService)
      .getKiMcp()
      .pipe(
        map((antwort) => antwort?.sichtbar === true),
        catchError(() => of(false)),
      ),
    { initialValue: false },
  );
  protected readonly kopiert = signal(false);

  private kopiertTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    inject(DestroyRef).onDestroy(() => clearTimeout(this.kopiertTimer));
  }

  kopieren(): void {
    void copyText(this.snippet).then((ok) => {
      if (ok) {
        this.zeigeKopiert();
      } else {
        this.snackBar.open(this.translate.instant('ki.mcp.kopierenFehler'), undefined, {
          panelClass: 'snackbar-error',
        });
      }
    });
  }

  private zeigeKopiert(): void {
    clearTimeout(this.kopiertTimer);
    this.kopiert.set(true);
    this.kopiertTimer = setTimeout(() => this.kopiert.set(false), KOPIERT_ANZEIGE_MS);
  }
}
